import { lazy, type ComponentType, type LazyExoticComponent } from "react";

type Module<T> = { default: T };

/**
 * React.lazy with a `preload()` handle.
 *
 * Once `preload()` has resolved, the lazy component resolves synchronously (the factory
 * hands React an already-settled thenable), so hydrating a prerendered page never
 * suspends and never discards the server markup.
 */
export function lazyPage<T extends ComponentType>(
  factory: () => Promise<Module<T>>,
): LazyExoticComponent<T> & { preload: () => Promise<Module<T>> } {
  let loaded: Module<T> | undefined;
  let pending: Promise<Module<T>> | undefined;

  const preload = () => {
    // A failed load is forgotten, so the next attempt (e.g. the lazy render) re-imports.
    pending ??= factory().then(
      (mod) => (loaded = mod),
      (err) => {
        pending = undefined;
        throw err;
      },
    );
    return pending;
  };

  const Component = lazy(() => {
    if (loaded) {
      const mod = loaded;
      // A synchronous thenable: React.lazy marks the component resolved immediately.
      return { then: (resolve: (m: Module<T>) => void) => resolve(mod) } as unknown as Promise<Module<T>>;
    }
    return preload();
  });

  return Object.assign(Component, { preload });
}
